import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import MessageBubble from "src/_modules/AiSupportChat/components/MessageBubble";
import { VISUALLY_HIDDEN } from "src/_modules/AiSupportChat/constants";
import { toPlainText } from "src/_modules/AiSupportChat/core/messageText";
import {
  MESSAGE_ROLES,
  MESSAGE_STATUS,
} from "src/_modules/AiSupportChat/core/protocol";

/** Closer than this to the bottom, new content keeps the list scrolled down (plan §3.6). */
const STICK_THRESHOLD_PX = 80;

/** Status lines announced when a live answer ends without completing. */
const ENDED_KEYS = Object.freeze({
  [MESSAGE_STATUS.ABORTED]: "message.aborted",
  [MESSAGE_STATUS.FAILED]: "message.failed",
  [MESSAGE_STATUS.INTERRUPTED]: "message.interrupted",
});

const keyOf = (message) => message.tempId ?? message.id;

/** Key of the answer being streamed from this tab, or null. */
const liveAnswerKey = (messages) => {
  const last = messages[messages.length - 1];
  return last?.tempId && last.status === MESSAGE_STATUS.STREAMING
    ? last.tempId
    : null;
};

/**
 * Screen-reader text for answers that just stopped streaming, or "". Only a
 * `streaming → *` change counts, so opening a conversation announces nothing.
 */
const endedAnswerText = (messages, previousStatuses, t) => {
  const ended = messages.filter(
    (message) =>
      message.role === MESSAGE_ROLES.ASSISTANT &&
      message.status !== MESSAGE_STATUS.STREAMING &&
      previousStatuses.get(keyOf(message)) === MESSAGE_STATUS.STREAMING
  );
  const last = ended[ended.length - 1];
  if (!last) return "";
  return last.status === MESSAGE_STATUS.COMPLETE
    ? toPlainText(last.content)
    : t(ENDED_KEYS[last.status] ?? "message.failed");
};

/**
 * The scrolling message list of a conversation (plan §3.6, §3.9):
 *
 * - Sticks to the bottom while the reader is within 80 px of it: opening
 *   lands on the latest message, and growing content (a streaming answer, a
 *   taller composer) keeps it in view. Scrolled further up, a «الانتقال إلى
 *   آخر رسالة» pill jumps back down. Sending a message always goes down.
 * - `role="log"` with `aria-live="off"`, so streamed tokens are never read
 *   out; a separate polite status region gets the text of an answer once it
 *   ends (C18). The log is focusable, so the keyboard can scroll it.
 *
 * @param {object} props
 * @param {Array<import("src/_modules/AiSupportChat/core/chatReducer").ChatMessage>} props.messages oldest first
 * @param {(messageId: string) => void} [props.onRetry] passed to every bubble; keep it stable
 * @param {(feedback: { messageId: string, rating: string }) => void} [props.onFeedbackSaved]
 *   passed to every bubble (👍 / 👎); keep it stable
 * @param {boolean} [props.showDebug] AI developers' debug data under the
 *   answers that carry it; never true for merchants
 * @param {string} props.label accessible name of the log (the conversation title)
 * @param {import("react").ReactNode} [props.intro] shown above the first message
 */
export default function MessageList({
  messages,
  onRetry,
  onFeedbackSaved,
  showDebug = false,
  label,
  intro,
}) {
  const { t } = useTranslation("aiSupport");
  const scrollerRef = useRef(null);
  const contentRef = useRef(null);
  // Follow new content; only the reader's own scrolling changes it
  const stickRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const [showJump, setShowJump] = useState(false);
  const statusesRef = useRef(null);
  const [announcement, setAnnouncement] = useState({ id: 0, text: "" });

  const scrollToBottom = useCallback(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, []);

  // Open at the latest message, before the first paint
  useLayoutEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  // A message sent from here (or a Retry) brings the reader down to its
  // answer, wherever they had scrolled
  const liveKey = liveAnswerKey(messages);
  useLayoutEffect(() => {
    if (!liveKey) return;
    stickRef.current = true;
    setShowJump(false);
    scrollToBottom();
  }, [liveKey, scrollToBottom]);

  // Any height change (new message, streamed text, fonts, a resized panel or
  // composer) keeps a reader at the bottom there
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (stickRef.current) scrollToBottom();
    });
    observer.observe(contentRef.current);
    observer.observe(scrollerRef.current);
    return () => observer.disconnect();
  }, [scrollToBottom]);

  useEffect(() => {
    const previous = statusesRef.current;
    statusesRef.current = new Map(
      messages.map((message) => [keyOf(message), message.status])
    );
    if (!previous) return;
    const text = endedAnswerText(messages, previous, t);
    if (text) setAnnouncement((current) => ({ id: current.id + 1, text }));
  }, [messages, t]);

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    const { scrollTop } = scroller;
    const distance = scroller.scrollHeight - scrollTop - scroller.clientHeight;
    // Only scrolling up lets go of the bottom: the scroll event of our own
    // jump can arrive after the next streamed chunk grew the list (big real
    // deltas), and must not read as the reader leaving
    const scrolledUp = scrollTop < lastScrollTopRef.current - 1;
    lastScrollTopRef.current = scrollTop;
    if (distance <= STICK_THRESHOLD_PX) stickRef.current = true;
    else if (scrolledUp) stickRef.current = false;
    else if (stickRef.current) scrollToBottom();
    setShowJump(!stickRef.current);
  };

  const jumpToLatest = () => {
    // The pill disappears at the bottom: keep focus in the panel (Esc)
    scrollerRef.current?.focus({ preventScroll: true });
    stickRef.current = true;
    setShowJump(false);
    scrollToBottom();
  };

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        ref={scrollerRef}
        role="log"
        aria-live="off"
        aria-label={label}
        tabIndex={0}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          "&:focus": { outline: "none" },
          "&:focus-visible": {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "-2px",
          },
        }}
      >
        <Stack ref={contentRef} spacing={1.5} sx={{ p: 2 }}>
          {intro}
          {messages.map((message) => (
            <MessageBubble
              key={keyOf(message)}
              message={message}
              onRetry={onRetry}
              onFeedbackSaved={onFeedbackSaved}
              showDebug={showDebug}
            />
          ))}
        </Stack>
      </Box>

      {showJump && (
        // Centred without translateX, which RTL wouldn't flip (C20)
        <Box
          sx={{
            position: "absolute",
            insetInline: 0,
            bottom: 12,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<KeyboardArrowDown />}
            onClick={jumpToLatest}
            sx={{
              pointerEvents: "auto",
              borderRadius: 999,
              boxShadow: 3,
              textTransform: "none",
            }}
          >
            {t("conversation.jumpToLatest")}
          </Button>
        </Box>
      )}

      <Box role="status" aria-live="polite" sx={VISUALLY_HIDDEN}>
        {announcement.text && (
          <span key={announcement.id}>{announcement.text}</span>
        )}
      </Box>
    </Box>
  );
}
